import { Button, Disclosure } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  contacts,
  externalReferences,
  offices,
  type Office,
} from "../../../api/addressBook.service";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "../../../components/Form/AsyncSearchableSelect";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import styles from "../AddressBook.module.css";
import { WizardStepper } from "../components/WizardStepper";
import introStyles from "../Contacts/WizardIntro.module.css";

type Step = "select" | "review" | "done";

const STEP_LABELS = ["Select Target", "Review", "Done"];

function stepperSteps(current: Step) {
  const order: Step[] = ["select", "review", "done"];
  const currentIndex = order.indexOf(current);
  return STEP_LABELS.map((label, i) => ({
    label,
    status:
      i < currentIndex
        ? ("complete" as const)
        : i === currentIndex
          ? ("active" as const)
          : ("pending" as const),
  }));
}

type SelectStepProps = {
  sourceOffice: Office;
  targetOfficeId: string | null;
  candidatesError: boolean;
  onTargetChange: (value: string | number | null) => void;
  onSearch: (query: string) => Promise<AsyncSelectOption[]>;
};

function SelectStep({
  sourceOffice,
  targetOfficeId,
  candidatesError,
  onTargetChange,
  onSearch,
}: SelectStepProps) {
  return (
    <div className={styles.wizardFormContainer}>
      <div className={`${styles.section} ${styles.sectionAccent}`}>
        <h3 className={styles.sectionTitle}>Source Office</h3>
        <div className={styles.reviewGrid}>
          <span className={styles.reviewLabel}>Name</span>
          <span className={styles.reviewValue}>{sourceOffice.name}</span>
          <span className={styles.reviewLabel}>City</span>
          <span className={styles.reviewValue}>
            {sourceOffice.city || <span className={styles.emptyText}>—</span>}
          </span>
          <span className={styles.reviewLabel}>Country</span>
          <span className={styles.reviewValue}>
            {sourceOffice.countryName || <span className={styles.emptyText}>—</span>}
          </span>
        </div>
      </div>

      <div className={styles.section}>
        <AsyncSearchableSelect
          name="target-office"
          label="Target Office"
          required
          value={targetOfficeId}
          onChange={onTargetChange}
          onSearch={onSearch}
          placeholder="Search for an office..."
          helperText="Select a target office to merge into"
          debounceMs={300}
          minSearchLength={0}
          error={candidatesError ? "Couldn't load offices — please retry." : undefined}
        />
      </div>
    </div>
  );
}

type ReviewStepProps = {
  sourceOffice: Office;
  targetOffice: Office;
  contactCount: number;
  externalReferenceCount: number;
  loadingCounts: boolean;
  error: string | null;
};

function ReviewStep({
  sourceOffice,
  targetOffice,
  contactCount,
  externalReferenceCount,
  loadingCounts,
  error,
}: ReviewStepProps) {
  if (loadingCounts) {
    return (
      <div className={styles.wizardFormContainer}>
        <LoadingState message="Loading details..." />
      </div>
    );
  }

  return (
    <div className={styles.wizardFormContainer}>
      <div className={styles.formGrid}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Merging: {sourceOffice.name}</h3>
          <div className={styles.summaryRow}>
            <span>
              {contactCount} contact{contactCount === 1 ? "" : "s"}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span>
              {externalReferenceCount} external reference{externalReferenceCount === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Into: {targetOffice.name}</h3>
          <p className={styles.helperText}>
            {targetOffice.city ? `${targetOffice.city}, ` : ""}
            {targetOffice.countryName || ""}
          </p>
        </div>
      </div>

      <div className={styles.warningBox}>
        <strong>Confirmation:</strong> All contacts and external references from{" "}
        <strong>{sourceOffice.name}</strong> will transfer to <strong>{targetOffice.name}</strong>.{" "}
        {sourceOffice.name} will be archived. This cannot be easily undone.
      </div>

      {error && <div className={styles.formAlert}>{error}</div>}
    </div>
  );
}

type DoneStepProps = {
  sourceOffice: Office;
  targetOffice: Office;
};

function DoneStep({ sourceOffice, targetOffice }: DoneStepProps) {
  return (
    <div className={styles.doneContainer}>
      <div className={styles.doneIconWrapper}>
        <span className={styles.doneIcon}>✓</span>
      </div>
      <h3 className={styles.doneTitle}>Merge Complete</h3>
      <p className={styles.doneMessage}>
        <strong>{sourceOffice.name}</strong> has been merged into{" "}
        <strong>{targetOffice.name}</strong>
      </p>
    </div>
  );
}

export default function OfficeMergeWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("select");
  const [sourceOffice, setSourceOffice] = useState<Office | null>(null);
  const [targetOfficeId, setTargetOfficeId] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<Office | null>(null);
  const [contactCount, setContactCount] = useState(0);
  const [externalReferenceCount, setExternalReferenceCount] = useState(0);
  const [loadingSource, setLoadingSource] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidatesError, setCandidatesError] = useState(false);
  const [effectiveDate] = useState(() => new Date().toISOString().split("T")[0]);

  useEffect(() => {
    if (!id) return;
    setLoadingSource(true);

    async function loadSource() {
      let office: Office | null = null;
      try {
        const officeRes = await offices.getById(id!);
        office = officeRes.data;
        setSourceOffice(office);
      } catch {
        setError("Failed to load office");
        setLoadingSource(false);
        return;
      }

      // Probe the candidates endpoint at mount time so a fetch failure surfaces
      // immediately rather than only when the user opens the dropdown.
      if (office.companyId) {
        try {
          await offices.getAll({ companyId: office.companyId });
        } catch {
          setCandidatesError(true);
        }
      }

      setLoadingSource(false);
    }

    loadSource();
  }, [id]);

  const handleSearch = useCallback(
    async (query: string): Promise<AsyncSelectOption[]> => {
      if (!sourceOffice) return [];
      setCandidatesError(false);
      try {
        const res = await offices.getAll({
          companyId: sourceOffice.companyId ?? undefined,
          search: query,
        });
        return res.data
          .filter(o => o.id !== sourceOffice.id && o.companyId === sourceOffice.companyId)
          .map(o => ({
            value: o.id,
            label: o.name,
            description: [o.city, o.countryName].filter(Boolean).join(", "),
          }));
      } catch {
        setCandidatesError(true);
        return [];
      }
    },
    [sourceOffice],
  );

  function handleTargetChange(value: string | number | null) {
    const officeId = value ? String(value) : null;
    setTargetOfficeId(officeId);
    if (officeId) {
      offices
        .getById(officeId)
        .then(res => setSelectedTarget(res.data))
        .catch(() => setSelectedTarget(null));
    } else {
      setSelectedTarget(null);
    }
  }

  async function loadCountsForReview(source: Office) {
    setLoadingCounts(true);
    try {
      const [contactsRes, extRefsRes] = await Promise.all([
        contacts.getAll({ officeId: source.id, limit: 500 }),
        externalReferences.getByEntity("office", source.id),
      ]);
      setContactCount(contactsRes.data.length);
      setExternalReferenceCount(extRefsRes.data.length);
    } catch {
      setError("Failed to load counts");
    } finally {
      setLoadingCounts(false);
    }
  }

  function goToReview() {
    if (!sourceOffice) return;
    setStep("review");
    loadCountsForReview(sourceOffice);
  }

  async function handleConfirm() {
    if (!sourceOffice || !selectedTarget) return;
    setSubmitting(true);
    setError(null);
    try {
      await offices.merge(sourceOffice.id, {
        targetOfficeId: selectedTarget.id,
        effectiveDate: `${effectiveDate}T00:00:00.000Z`,
      });
      setStep("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Merge failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingSource) return <LoadingState message="Loading office..." />;
  if (!sourceOffice) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Merge Office" />
        <div className={styles.formAlert}>{error || "Office not found"}</div>
      </div>
    );
  }

  return (
    <div className={styles.wizardWrapper}>
      <PageHeader title="Merge Office" subtitle={sourceOffice.name} />

      <WizardStepper steps={stepperSteps(step)} />

      <div className={styles.wizardContent}>
        {step === "select" && (
          <div className={introStyles.intro}>
            <p className={introStyles.oneLiner}>
              Merges this office into another office at the same firm.
            </p>
            <Disclosure summary="How it works">
              <p className={introStyles.detail}>
                All contacts and external references attached to this office are transferred to the
                target office. This office is then archived. Can be reversed later via Undo Merge.
              </p>
            </Disclosure>
          </div>
        )}

        {step === "select" && (
          <SelectStep
            sourceOffice={sourceOffice}
            targetOfficeId={targetOfficeId}
            candidatesError={candidatesError}
            onTargetChange={handleTargetChange}
            onSearch={handleSearch}
          />
        )}

        {step === "review" && selectedTarget && (
          <ReviewStep
            sourceOffice={sourceOffice}
            targetOffice={selectedTarget}
            contactCount={contactCount}
            externalReferenceCount={externalReferenceCount}
            loadingCounts={loadingCounts}
            error={error}
          />
        )}

        {step === "done" && selectedTarget && (
          <DoneStep sourceOffice={sourceOffice} targetOffice={selectedTarget} />
        )}
      </div>

      <div className={styles.wizardActions}>
        {step === "select" && (
          <>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.officeDetail(id!))}
            >
              Cancel
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={goToReview}
                disabled={!selectedTarget}
              >
                Next
              </Button>
            </div>
          </>
        )}

        {step === "review" && (
          <>
            <Button variant="outlined" color="default" onClick={() => setStep("select")}>
              Back
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={submitting || loadingCounts}
              >
                {submitting ? "Merging..." : "Merge Offices"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && (
          <div className={styles.wizardActionsRight}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.offices)}
            >
              Back to Offices
            </Button>
            {selectedTarget && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(ROUTES.addressBook.officeDetail(selectedTarget.id))}
              >
                View {selectedTarget.name}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
